import { useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { motion, useScroll, useMotionValue, useSpring } from "framer-motion";
import Lenis from "lenis";
import { Toaster, toast } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { CartProvider } from "@/context/CartContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CartDrawer from "@/components/CartDrawer";
import Home from "@/pages/Home";
import Store from "@/pages/Store";
import About from "@/pages/About";
import Join from "@/pages/Join";
import Login from "@/pages/Login";
import Members from "@/pages/Members";
import Gallery from "@/pages/Gallery";
import Admin from "@/pages/Admin";
import Devotions from "@/pages/Devotions";
import Guest from "@/pages/Guest";
import Bylaws from "@/pages/Bylaws";
import ResetPassword from "@/pages/ResetPassword";
import Donate from "@/pages/Donate";
import PaymentResult from "@/pages/PaymentResult";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function SmoothScroll() {
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    let raf;
    const loop = (t) => { lenis.raf(t); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); };
  }, []);
  return null;
}

function ScrollProgress() {
  const { scrollYProgress } = useScroll();
  return (
    <motion.div
      className="fixed top-0 left-0 right-0 h-[3px] z-[60] origin-left bg-gradient-to-r from-gold via-crimson to-gold"
      style={{ scaleX: scrollYProgress }}
      aria-hidden="true"
    />
  );
}

function CursorGlow() {
  const x = useMotionValue(-600);
  const y = useMotionValue(-600);
  const sx = useSpring(x, { stiffness: 55, damping: 18 });
  const sy = useSpring(y, { stiffness: 55, damping: 18 });

  useEffect(() => {
    if (window.matchMedia("(pointer: coarse)").matches) return;
    const handler = (e) => { x.set(e.clientX); y.set(e.clientY); };
    window.addEventListener("mousemove", handler);
    return () => window.removeEventListener("mousemove", handler);
  }, [x, y]);

  if (typeof window !== "undefined" && window.matchMedia("(pointer: coarse)").matches) return null;
  return (
    <motion.div
      className="pointer-events-none fixed z-[45] w-[480px] h-[480px] rounded-full mix-blend-screen"
      style={{
        left: sx, top: sy, translateX: "-50%", translateY: "-50%",
        background: "radial-gradient(circle, rgba(212,175,55,0.07) 0%, rgba(178,34,34,0.04) 35%, transparent 65%)",
      }}
      aria-hidden="true"
    />
  );
}

function AuthCallback() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const sessionId = location.hash.split("session_id=")[1]?.split("&")[0];
    if (!sessionId) {
      setUser(false);
      navigate("/login", { replace: true });
      return;
    }
    api.post("/auth/google/session", { session_id: sessionId })
      .then(({ data }) => {
        setUser(data);
        window.history.replaceState(null, "", window.location.pathname);
        navigate("/members", { replace: true, state: { user: data } });
      })
      .catch(() => {
        setUser(false);
        toast.error("Google sign-in failed. Please try again.");
        navigate("/login", { replace: true });
      });
  }, [setUser, navigate, location.hash]);

  return (
    <main className="pt-16 min-h-screen flex items-center justify-center" data-testid="auth-callback">
      <p className="font-mono text-sm text-gold tracking-[0.3em] uppercase animate-pulse">Signing you in...</p>
    </main>
  );
}

function AppRoutes() {
  const location = useLocation();
  // Check URL fragment (not query params) for session_id — synchronous during render to prevent race conditions
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/store" element={<Store />} />
      <Route path="/about" element={<About />} />
      <Route path="/join" element={<Join />} />
      <Route path="/login" element={<Login />} />
      <Route path="/members" element={<Members />} />
      <Route path="/gallery" element={<Gallery />} />
      <Route path="/devotions" element={<Devotions />} />
      <Route path="/guest/:code" element={<Guest />} />
      <Route path="/bylaws" element={<Bylaws />} />
      <Route path="/donate" element={<Donate />} />
      <Route path="/admin" element={<Admin />} />
      <Route path="/reset-password/:token" element={<ResetPassword />} />
      <Route path="/payment/success" element={<PaymentResult variant="success" />} />
      <Route path="/payment/cancel" element={<PaymentResult variant="cancel" />} />
      <Route path="*" element={<Home />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <CartProvider>
            <SmoothScroll />
            <ScrollToTop />
            <ScrollProgress />
            <CursorGlow />
            <Header />
            <CartDrawer />
            <AppRoutes />
            <Footer />
            <Toaster theme="dark" position="bottom-right" toastOptions={{ style: { background: "#13161F", border: "1px solid rgba(212,175,55,0.3)", color: "#F7F5F0", borderRadius: 0 } }} />
          </CartProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
